import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles, Zap, Clock, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_WORDS = ["amazing", "premium", "bold", "fast", "scalable"];

export default function CTASection({ profile, settings }) {
    const rotatingWords = settings?.rotating_words?.length ? settings.rotating_words : FALLBACK_WORDS;
    const headlinePrefix = settings?.cta_headline_prefix || "Build something";
    const subheadline = settings?.cta_subheadline || "Architecting high-reliability software infrastructure and intelligent web applications. Open for strategic discussions on engineering scalable digital solutions. Schedule an introductory consultation.";
    const responseTime = settings?.response_time_label || "24 hours";
    const deliveryTime = settings?.delivery_time_label || "2 – 4 weeks";
    const slotsLabel = settings?.slots_label || "2 of 3 open";
    const calendarUrl = settings?.calendar_url || "#contact";
    const [wordIdx, setWordIdx] = useState(0);
    const btnRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        const t = setInterval(() => setWordIdx((i) => (i + 1) % rotatingWords.length), 1800);
        return () => clearInterval(t);
    }, [rotatingWords.length]);

    // Magnetic button effect
    useEffect(() => {
        const wrap = wrapRef.current;
        const btn = btnRef.current;
        if (!wrap || !btn) return;
        const onMove = (e) => {
            const rect = wrap.getBoundingClientRect();
            const x = e.clientX - rect.left - rect.width / 2;
            const y = e.clientY - rect.top - rect.height / 2;
            btn.style.transform = `translate(${x * 0.25}px, ${y * 0.25}px)`;
        };
        const onLeave = () => {
            btn.style.transform = "translate(0, 0)";
        };
        wrap.addEventListener("mousemove", onMove);
        wrap.addEventListener("mouseleave", onLeave);
        return () => {
            wrap.removeEventListener("mousemove", onMove);
            wrap.removeEventListener("mouseleave", onLeave);
        };
    }, []);

    return (
        <section
            id="cta"
            data-testid="cta-section"
            className="relative py-32 md:py-40 border-t border-white/5 overflow-hidden"
        >
            {/* Animated gradient backdrop */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-brand/25 blur-[180px] animate-pulse" style={{ animationDuration: "4s" }} />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-brand/15 blur-[160px] animate-pulse" style={{ animationDuration: "6s", animationDelay: "1s" }} />
            </div>
            <div className="absolute inset-0 bg-grid opacity-15 pointer-events-none" aria-hidden />

            <div className="relative max-w-6xl mx-auto px-6 lg:px-10 text-center">
                <div className="mono-label mb-6 inline-flex items-center gap-2">
                    <Sparkles size={11} className="text-brand" /> 06 · Let&apos;s talk
                </div>

                <h2 className="font-display text-5xl sm:text-7xl lg:text-[9rem] xl:text-[10rem] text-white leading-[0.9] tracking-tighter">
                    {headlinePrefix}
                </h2>
                <div
                    className="font-display text-5xl sm:text-7xl lg:text-[9rem] xl:text-[10rem] leading-[0.9] tracking-tighter h-[1em] overflow-hidden inline-block"
                    aria-live="polite"
                >
                    <span
                        key={wordIdx}
                        className="inline-block text-brand"
                        style={{
                            animation: "slideUpFade 1.8s cubic-bezier(0.22, 1, 0.36, 1) infinite",
                        }}
                        data-testid="cta-rotating-word"
                    >
                        {rotatingWords[wordIdx]}.
                    </span>
                </div>

                <p className="mt-10 text-lg sm:text-xl text-ink-100 max-w-2xl mx-auto leading-relaxed">
                    {subheadline}
                </p>

                <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                    <div ref={wrapRef} className="relative inline-flex" style={{ padding: "20px" }}>
                        <a
                            ref={btnRef}
                            href={calendarUrl}
                            target={calendarUrl?.startsWith("http") ? "_blank" : undefined}
                            rel={calendarUrl?.startsWith("http") ? "noreferrer" : undefined}
                            data-testid="cta-magnetic-button"
                            onClick={(e) => {
                                if (calendarUrl === "#contact" || calendarUrl?.startsWith("#")) {
                                    e.preventDefault();
                                    document.querySelector(calendarUrl)?.scrollIntoView({ behavior: "smooth" });
                                }
                            }}
                            className="group relative inline-flex items-center gap-3 px-9 py-4 rounded-full bg-brand text-white font-medium text-lg hover:bg-brand-hover transition-[background-color,box-shadow] duration-300 shadow-[0_20px_60px_-10px_rgba(255,69,0,0.6)] hover:shadow-[0_20px_80px_-10px_rgba(255,69,0,0.8)] will-change-transform"
                            style={{ transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.3s, box-shadow 0.3s" }}
                        >
                            <span>Book a free 15-min call</span>
                            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </a>
                    </div>
                    {profile?.email && (
                        <div className="inline-flex items-center gap-2">
                            <a
                                href={`mailto:${profile.email}?subject=Project%20inquiry`}
                                data-testid="cta-email-direct"
                                className="inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors"
                            >
                                Or email directly
                            </a>
                            <button
                                type="button"
                                onClick={() => {
                                    navigator.clipboard.writeText(profile.email);
                                    toast.success("Email copied to clipboard!");
                                }}
                                title="Copy email address"
                                aria-label="Copy email address"
                                className="p-4 rounded-full border border-white/15 text-ink-200 hover:text-white hover:border-brand/40 hover:bg-white/5 transition-colors"
                            >
                                <Copy size={18} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Trust signals */}
                <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                    <Signal icon={Clock} label="Reply within" value={responseTime} />
                    <Signal icon={Zap} label="MVP delivery" value={deliveryTime} />
                    <Signal icon={Sparkles} label="Slots this month" value={slotsLabel} />
                </div>
            </div>

            <style>{`
                @keyframes slideUpFade {
                    0% { transform: translateY(100%); opacity: 0; }
                    15%, 85% { transform: translateY(0); opacity: 1; }
                    100% { transform: translateY(-100%); opacity: 0; }
                }
            `}</style>
        </section>
    );
}

function Signal({ icon: Icon, label, value }) {
    return (
        <div className="p-5 rounded-xl border border-white/5 bg-ink-800/30 backdrop-blur-md">
            <div className="flex items-center justify-center gap-2 text-brand mb-2">
                <Icon size={16} />
            </div>
            <div className="mono-label">{label}</div>
            <div className="text-white font-medium mt-1">{value}</div>
        </div>
    );
}
